import React, { useCallback, useEffect, useState } from 'react';
import { Button, Dropdown } from 'semantic-ui-react';
import { SemanticToastContainer, toast } from 'react-semantic-toasts';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../providers/AuthenticationProvider';
import { useComponentManager } from '../../providers/ComponentManagerProvider';
import { ComponentNavigator } from './ComponentNavigator';
import { Breadcrumb } from './Breadcrumb';
import { ComponentDetailPanel } from './ComponentDetailPanel';
import { ScheduleTab } from './ScheduleTab';
import { StructureTab } from './StructureTab';
import { MappingTab } from './MappingTab';
import { ComponentModal } from './ComponentModal';
import { ComponentDeleteConfirmation } from './ComponentDeleteConfirmation';
import './ComponentManager.css';

const TABS = {
    MANAGER: 'manager',
    SCHEDULE: 'schedule',
    STRUCTURE: 'structure',
    MAPPING: 'mapping',
};

const TAB_OPTIONS = [
    { key: TABS.MANAGER, text: 'Manager', value: TABS.MANAGER },
    { key: TABS.SCHEDULE, text: 'Schedule Tree', value: TABS.SCHEDULE },
    { key: TABS.STRUCTURE, text: 'Structure Tree', value: TABS.STRUCTURE },
    { key: TABS.MAPPING, text: 'Mapping Tree', value: TABS.MAPPING },
];

const TAB_TITLES = {
    [TABS.MANAGER]: 'Component Manager',
    [TABS.SCHEDULE]: 'Schedule Tree',
    [TABS.STRUCTURE]: 'Structure Tree',
    [TABS.MAPPING]: 'Mapping Tree',
};

export const ComponentManager = () => {
    const navigate = useNavigate();
    const { isSystemAdmin, isPathianTech } = useAuth();
    const {
        columns,
        selectedPath,
        selectComponent,
        loadingColumnIndex,
        loadRoots,
        selectedComponent,
        refreshColumn,
        patchComponent,
        handleComponentDeleted,
        getComponentDeleteImpact,
    } = useComponentManager();
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState(TABS.MANAGER);
    const [modalOpen, setModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState('create');
    const [modalParentId, setModalParentId] = useState(null);
    const [modalComponentId, setModalComponentId] = useState(null);
    const [detailRefreshKey, setDetailRefreshKey] = useState(0);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleteImpact, setDeleteImpact] = useState(null);
    const [deleteImpactError, setDeleteImpactError] = useState(false);
    const [preparingDelete, setPreparingDelete] = useState(false);

    const isAuthorized = isSystemAdmin() || isPathianTech();

    const openAddRoot = () => {
        setModalMode('create');
        setModalParentId(null);
        setModalOpen(true);
    };

    const openAddChild = () => {
        if (!selectedComponent) return;
        setModalMode('create');
        setModalParentId(selectedComponent.id);
        setModalOpen(true);
    };

    const openEdit = () => {
        if (!selectedComponent) return;
        setModalMode('edit');
        setModalComponentId(selectedComponent.id);
        setModalOpen(true);
    };

    const openDelete = async () => {
        if (!selectedComponent || preparingDelete) return;
        const target = selectedComponent;
        setDeleteTarget(target);
        setDeleteImpact(null);
        setDeleteImpactError(false);
        setPreparingDelete(true);
        try {
            const res = await getComponentDeleteImpact(target.id);
            setDeleteImpact(res?.data ?? null);
            setDeleteImpactError(res?.data == null);
        } catch {
            setDeleteImpactError(true);
        } finally {
            setPreparingDelete(false);
            setDeleteModalOpen(true);
        }
    };

    const handleDeleted = () => {
        handleComponentDeleted()
            .then(() =>
                toast({
                    type: 'success',
                    title: 'Success',
                    description: 'Component deleted.',
                    animation: 'fade down',
                    time: 4000,
                })
            )
            .catch(() =>
                showError('The component was deleted, but the tree failed to refresh.')
            );
    };

    const handleCreated = async () => {
        if (modalParentId == null) {
            await refreshColumn(null);
        } else {
            await refreshColumn(modalParentId);
            patchComponent({ id: modalParentId, has_dependents: true });
        }
        toast({
            type: 'success',
            title: 'Success',
            description: 'Component created.',
            animation: 'fade down',
            time: 4000,
        });
    };

    const handleUpdated = async updated => {
        if (updated) patchComponent({ ...updated, name: updated.description });
        setDetailRefreshKey(k => k + 1);
        toast({
            type: 'success',
            title: 'Success',
            description: 'Component updated.',
            animation: 'fade down',
            time: 4000,
        });
    };

    const handleEditMissing = () => {
        const parentId = selectedPath[selectedPath.length - 2]?.id ?? null;
        refreshColumn(parentId).catch(() => {});
        setDetailRefreshKey(k => k + 1);
        showError('This component no longer exists. It may have been deleted.');
    };

    const showError = useCallback(
        description =>
            toast({
                type: 'error',
                title: 'Error',
                description,
                animation: 'fade down',
                time: 8000,
            }),
        []
    );

    useEffect(() => {
        if (!isAuthorized) {
            navigate('/');
        }
    }, [isAuthorized, navigate]);

    useEffect(() => {
        // Do not fetch (or toast) for users who are about to be redirected away.
        if (!isAuthorized) return;
        let cancelled = false;
        setLoading(true);
        loadRoots()
            .then(() => {
                if (!cancelled) setLoading(false);
            })
            .catch(() => {
                if (cancelled) return;
                setLoading(false);
                showError('Failed to load components.');
            });
        return () => {
            cancelled = true;
        };
    }, [isAuthorized, loadRoots, showError]);

    if (!isAuthorized) {
        return null;
    }

    return (
        <div className="component-manager-container">
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    width: '100%',
                }}
            >
                <div style={{ fontSize: '35px', color: '#153A4B' }} className="pageTitle">
                    <p style={{ margin: '15px 15px 15px 0px' }}>{TAB_TITLES[activeTab]}</p>
                </div>
                <div
                    className="component-manager-tab-controls"
                    style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}
                >
                    {activeTab === TABS.STRUCTURE ? (
                        <Button primary onClick={openAddRoot} data-testid="add-root-button">
                            Add Root Component
                        </Button>
                    ) : null}
                    <Dropdown
                        selection
                        options={TAB_OPTIONS}
                        value={activeTab}
                        onChange={(e, { value }) => setActiveTab(value)}
                        data-testid="tab-dropdown"
                    />
                </div>
            </div>

            {activeTab === TABS.MANAGER && (
                <>
                    <Breadcrumb onError={() => showError('Failed to load component children.')} />
                    <ComponentNavigator
                        columns={columns}
                        selectedPath={selectedPath}
                        onSelect={selectComponent}
                        loadingColumnIndex={loadingColumnIndex}
                        loading={loading}
                        onError={() => showError('Failed to load component children.')}
                    />
                    <ComponentDetailPanel />
                </>
            )}

            {activeTab === TABS.STRUCTURE && (
                <StructureTab
                    onError={showError}
                    onAddChild={openAddChild}
                    onEdit={openEdit}
                    onDelete={openDelete}
                    deleting={preparingDelete}
                    detailRefreshKey={detailRefreshKey}
                />
            )}

            {activeTab === TABS.SCHEDULE && <ScheduleTab onError={showError} />}

            {activeTab === TABS.MAPPING && <MappingTab onError={showError} />}

            <ComponentModal
                open={modalOpen}
                setOpen={setModalOpen}
                mode={modalMode}
                parentId={modalParentId}
                componentId={modalComponentId}
                onCreated={handleCreated}
                onUpdated={handleUpdated}
                onMissing={handleEditMissing}
            />

            <ComponentDeleteConfirmation
                open={deleteModalOpen}
                setOpen={setDeleteModalOpen}
                component={deleteTarget}
                impact={deleteImpact}
                impactError={deleteImpactError}
                onDeleted={handleDeleted}
            />

            <SemanticToastContainer position="bottom-center" />
        </div>
    );
};
